import React from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EnTimelineView from './views/TimelineView';
import EnFavoritesView from './views/FavoritesView';
import EnKeywordView from './views/KeywordView';
import EnDiscoveryView from './views/DiscoveryView';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { useData } from '../../context/DataContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';

const EnMainContent = ({ 
  currentView, 
  conversations, 
  themes,
  tags,
  favorites,
  selectedConversation,
  onConversationClick,
  searchQuery,
  filters,
  onFilterChange,
  onToggleFavorite,
  onSearch,
  onNavItemClick
}) => {
  const theme = useTheme();
  // Get view history and record function from data context
  const { recordView } = useData();
  
  // Function to record view history when conversation is clicked
  // Only record explicit user actions (clicks)
  const handleConversationClick = (conversation) => {
    if (conversation) {
      // Record view history
      recordView(conversation.id);
    }
    // Execute original processing
    onConversationClick(conversation);
  };

  // Display entire context if a conversation is selected
  if (selectedConversation) {
    return (
      <Box 
        component={Paper} 
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          overflow: 'auto',
          m: 1,
          borderRadius: 1,
          position: 'relative'
        }}
      >
        {/* Action buttons */}
        <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 100, display: 'flex', gap: 1 }}>
          {/* Back button */}
          <IconButton 
            onClick={() => onConversationClick(null)}
            color="primary"
            aria-label="Return to timeline view"
            sx={{
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          {/* Favorite button */}
          <Tooltip title={favorites && favorites.includes(selectedConversation.id) ? "Remove from favorites" : "Add to favorites"}>
            <IconButton
              onClick={() => onToggleFavorite && onToggleFavorite(selectedConversation.id)}
              color={favorites && favorites.includes(selectedConversation.id) ? "warning" : "default"}
              aria-label={favorites && favorites.includes(selectedConversation.id) ? "Remove from favorites" : "Add to favorites"}
              sx={{
                bgcolor: 'white',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                }
              }}
            >
              {favorites && favorites.includes(selectedConversation.id) ? <StarIcon /> : <StarOutlineIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ mt: 5 }}>  {/* Place content below buttons */}
          <Box sx={{ 
            '& p': { margin: '0.8em 0' },
            '& pre': { 
              backgroundColor: alpha(theme.palette.text.primary, 0.05),
              padding: '12px',
              borderRadius: '8px',
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`
            },
            '& code': {
              backgroundColor: alpha(theme.palette.text.primary, 0.1),
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.9em',
              fontFamily: 'monospace'
            },
            '& blockquote': {
              borderLeft: `4px solid ${theme.palette.divider}`,
              paddingLeft: '16px',
              margin: '16px 0',
              fontStyle: 'italic',
              color: 'text.secondary'
            },
            '& ul, & ol': { 
              paddingLeft: '24px',
              margin: '8px 0'
            },
            '& li': {
              margin: '4px 0'
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': { 
              margin: '1.2em 0 0.6em 0',
              fontWeight: 'bold',
              lineHeight: 1.3
            },
            '& h1': { fontSize: '2em', borderBottom: `2px solid ${theme.palette.divider}`, paddingBottom: '8px' },
            '& h2': { fontSize: '1.5em', borderBottom: `1px solid ${theme.palette.divider}`, paddingBottom: '4px' },
            '& h3': { fontSize: '1.3em' },
            '& table': {
              border: `1px solid ${theme.palette.divider}`,
              borderCollapse: 'collapse',
              margin: '16px 0',
              width: '100%'
            },
            '& th, & td': {
              border: `1px solid ${theme.palette.divider}`,
              padding: '8px 12px',
              textAlign: 'left'
            },
            '& th': {
              backgroundColor: theme.palette.action.hover,
              fontWeight: 'bold'
            },
            '& hr': {
              border: 'none',
              borderTop: `2px solid ${theme.palette.divider}`,
              margin: '24px 0'
            }
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
            >
              {selectedConversation.content || ''}
            </ReactMarkdown>
          </Box>
          
          {selectedConversation.imageData && (
            <Box sx={{ mt: 2 }}>
              <img 
                src={selectedConversation.imageData} 
                alt="Context" 
                style={{ maxWidth: '100%', maxHeight: '300px' }} 
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Display component based on current view
  const renderCurrentView = () => {
    switch (currentView) {
      case 'timeline':
        return (
          <EnTimelineView 
            conversations={conversations} 
            onConversationClick={handleConversationClick}
            searchQuery={searchQuery}
            filters={filters}
            onFilterChange={onFilterChange}
            favorites={favorites}
            onToggleFavorite={onToggleFavorite}
            onSearch={onSearch}
          />
        );
      case 'favorites':
        return (
          <EnFavoritesView 
            conversations={conversations} 
            onConversationClick={handleConversationClick}
            searchQuery={searchQuery}
            favorites={favorites}
            onToggleFavorite={onToggleFavorite}
            onSearch={onSearch}
          />
        );
            case 'keywords':
        return (
          <EnKeywordView 
            conversations={conversations} 
            tags={tags}
            onConversationClick={handleConversationClick}
            searchQuery={searchQuery}
            onSearch={onSearch}
            onNavItemClick={onNavItemClick}
          />
        );
      case 'discovery':
        return (
          <EnDiscoveryView 
            conversations={conversations} 
            themes={themes}
            onConversationClick={handleConversationClick}
            searchQuery={searchQuery}
            onSearch={onSearch}
            onNavItemClick={onNavItemClick}
          />
        );
      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" color="text.secondary">
              Select a view from the navigation menu
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box 
      component={Paper} 
      sx={{ 
        flexGrow: 1, 
        p: 2, 
        overflow: 'auto',
        m: 1,
        borderRadius: 1,
      }}
    >
      {renderCurrentView()}
    </Box>
  );
};

export default EnMainContent; 